import api from './api';

/**
 * Satellite Data Service
 * Fetches real-time satellite, weather, flood, and fire data
 * Integrates with NASA FIRMS, Google APIs, and Open-Meteo
 */

export const satelliteApi = {
  /**
   * Get live fire data from NASA FIRMS
   */
  getFireData: async (source = 'VIIRS_NOAA20_NRT', days = 1) => {
    try {
      const response = await api.get('/satellite/fires', { params: { source, days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching fire data:', error);
      throw error;
    }
  },

  /**
   * Get available NASA FIRMS data sources
   */
  getAvailableSources: async () => {
    try {
      const response = await api.get('/satellite/fires/sources');
      return response.data;
    } catch (error) {
      console.error('Error fetching available sources:', error);
      throw error;
    }
  },

  /**
   * Get fires near a specific location
   */
  getFiresNearby: async (lat, lng, radius = 50, days = 7) => {
    try {
      const response = await api.get('/satellite/fires/nearby', {
        params: { lat, lng, radius, days }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching nearby fires:', error);
      throw error;
    }
  },

  /**
   * Get current weather for a location
   */
  getWeather: async (lat, lng) => {
    const response = await api.get('/satellite/weather', {
      params: { lat, lng }
    });
    return response.data;
  },

  /**
   * Get weather forecast
   */
  getWeatherForecast: async (lat, lng) => {
    const response = await api.get('/satellite/weather/forecast', {
      params: { lat, lng }
    });
    return response.data;
  },

  /**
   * Get fire risk assessment
   */
  getFireRisk: async (lat, lng) => {
    const response = await api.get('/satellite/fire-risk', {
      params: { lat, lng }
    });
    return response.data;
  },

  /**
   * Get elevation for a location
   */
  getElevation: async (lat, lng) => {
    const response = await api.get('/satellite/elevation', {
      params: { lat, lng }
    });
    return response.data;
  },

  /**
   * Get terrain analysis
   */
  getTerrainAnalysis: async (lat, lng, radius = 5) => {
    const response = await api.get('/satellite/terrain', {
      params: { lat, lng, radius }
    });
    return response.data;
  },

  /**
   * Get NDVI estimate
   */
  getNDVI: async (lat, lng, season = 'summer') => {
    const response = await api.get('/satellite/ndvi', {
      params: { lat, lng, season }
    });
    return response.data;
  },

  /**
   * Get NDVI change detection
   */
  getNDVIChange: async (beforeNDVI, afterNDVI) => {
    const response = await api.get('/satellite/ndvi/change', {
      params: { beforeNDVI, afterNDVI }
    });
    return response.data;
  },

  /**
   * Get satellite imagery URL
   */
  getImageryUrl: async (lat, lng, date, layer) => {
    const response = await api.get('/satellite/imagery', {
      params: { lat, lng, date, layer }
    });
    return response.data;
  },

  /**
   * Get comprehensive analysis for a location
   */
  getComprehensiveAnalysis: async (lat, lng) => {
    const response = await api.get('/satellite/analysis', {
      params: { lat, lng }
    });
    return response.data;
  },

  // ==========================================
  // NASA JPL APIs - LIVE DATA (Always works!)
  // ==========================================

  /**
   * Get fireball/meteor data from NASA JPL (LIVE)
   */
  getFireballs: async (limit = 50) => {
    try {
      const response = await api.get('/satellite/fireballs', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching fireballs:', error);
      return { success: false, fireballs: [], count: 0 };
    }
  },

  /**
   * Get fireballs near a location (LIVE)
   */
  getFireballsNearby: async (lat, lng, radius = 1000) => {
    try {
      const response = await api.get('/satellite/fireballs/nearby', {
        params: { lat, lng, radius }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching nearby fireballs:', error);
      return { success: false, fireballs: [], count: 0 };
    }
  },

  /**
   * Get asteroid impact monitoring data from NASA Sentry (LIVE)
   */
  getAsteroids: async () => {
    try {
      const response = await api.get('/satellite/asteroids');
      return response.data;
    } catch (error) {
      console.error('Error fetching asteroids:', error);
      return { success: false, objects: [], count: 0 };
    }
  },

  /**
   * Get combined space hazards (fireballs + asteroids) (LIVE)
   */
  getSpaceHazards: async () => {
    try {
      const response = await api.get('/satellite/space-hazards');
      return response.data;
    } catch (error) {
      console.error('Error fetching space hazards:', error);
      return { success: false, fireballs: [], asteroids: [] };
    }
  },

  // ==========================================
  // FLOOD MONITORING APIs (Google API Powered)
  // ==========================================

  /**
   * Get flood risk assessment for a location
   */
  getFloodRisk: async (lat, lng, radius = 1) => {
    try {
      const response = await api.get('/satellite/flood-risk', {
        params: { lat, lng, radius }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching flood risk:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get elevation for flood analysis (Google Elevation API)
   */
  getFloodElevation: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/flood-elevation', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching flood elevation:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get nearby water bodies
   */
  getNearbyWaterBodies: async (lat, lng, radius = 5000) => {
    try {
      const response = await api.get('/satellite/water-bodies', {
        params: { lat, lng, radius }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching water bodies:', error);
      return { success: false, waterBodies: [], count: 0 };
    }
  },

  /**
   * Get historical flood data
   */
  getFloodHistory: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/flood-history', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching flood history:', error);
      return { success: false, historicalEvents: [] };
    }
  },

  /**
   * Get elevation profile for water flow analysis
   */
  getElevationProfile: async (points) => {
    try {
      const response = await api.post('/satellite/elevation-profile', { points });
      return response.data;
    } catch (error) {
      console.error('Error fetching elevation profile:', error);
      return { success: false, error: error.message };
    }
  },

  // ==========================================
  // ENHANCED WEATHER APIs (Google API Powered)
  // ==========================================

  /**
   * Get enhanced weather with Google location data
   */
  getEnhancedWeather: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/weather-enhanced', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching enhanced weather:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get 7-day enhanced forecast
   */
  getEnhancedForecast: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/forecast-enhanced', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching enhanced forecast:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get air quality data
   */
  getAirQuality: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/air-quality', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching air quality:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get historical weather data
   */
  getWeatherHistory: async (lat, lng, startDate, endDate) => {
    try {
      const response = await api.get('/satellite/weather-history', {
        params: { lat, lng, startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching weather history:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get enhanced fire weather risk
   */
  getEnhancedFireRisk: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/fire-risk-enhanced', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching enhanced fire risk:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get location details (Google Geocoding)
   */
  getLocationDetails: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/location', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching location details:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get comprehensive disaster analysis (fires, floods, weather)
   */
  getDisasterAnalysis: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/disaster-analysis', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching disaster analysis:', error);
      return { success: false, error: error.message };
    }
  },

  // ==========================================
  // GOOGLE EARTH ENGINE APIs
  // ==========================================

  /**
   * Get NDVI using Google Earth Engine (Landsat 8)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  getGeeNDVI: async (lat, lng, startDate, endDate) => {
    try {
      const response = await api.get('/satellite/gee/ndvi', {
        params: { lat, lng, startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE NDVI:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get NDVI time series using Google Earth Engine
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} interval - 'weekly', 'monthly', or default 16-day
   */
  getGeeNDVITimeSeries: async (lat, lng, startDate, endDate, interval = 'monthly') => {
    try {
      const response = await api.get('/satellite/gee/ndvi/timeseries', {
        params: { lat, lng, startDate, endDate, interval }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE NDVI time series:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get greenest pixel composite using quality mosaicking
   * Selects pixels with maximum NDVI values
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {number} radiusKm - Analysis radius in kilometers
   */
  getGeeGreenestPixel: async (lat, lng, startDate, endDate, radiusKm = 10) => {
    try {
      const response = await api.get('/satellite/gee/greenest', {
        params: { lat, lng, startDate, endDate, radiusKm }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE greenest pixel:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get median composite for cloud-free visualization
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {number} radiusKm - Analysis radius in kilometers
   */
  getGeeMedianComposite: async (lat, lng, startDate, endDate, radiusKm = 10) => {
    try {
      const response = await api.get('/satellite/gee/median', {
        params: { lat, lng, startDate, endDate, radiusKm }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE median composite:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Analyze vegetation change between two time periods
   * Useful for disaster damage assessment
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} beforeStart - Before period start date
   * @param {string} beforeEnd - Before period end date
   * @param {string} afterStart - After period start date
   * @param {string} afterEnd - After period end date
   */
  getGeeVegetationChange: async (lat, lng, beforeStart, beforeEnd, afterStart, afterEnd) => {
    try {
      const response = await api.get('/satellite/gee/vegetation-change', {
        params: { lat, lng, beforeStart, beforeEnd, afterStart, afterEnd }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE vegetation change:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get Sentinel-2 NDVI (10m resolution - higher detail)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  getGeeSentinel2NDVI: async (lat, lng, startDate, endDate) => {
    try {
      const response = await api.get('/satellite/gee/sentinel2/ndvi', {
        params: { lat, lng, startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Sentinel-2 NDVI:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get MODIS NDVI for regional analysis (250m resolution, daily updates)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {number} radiusKm - Analysis radius in kilometers
   */
  getGeeMODISNDVI: async (lat, lng, startDate, endDate, radiusKm = 25) => {
    try {
      const response = await api.get('/satellite/gee/modis/ndvi', {
        params: { lat, lng, startDate, endDate, radiusKm }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching MODIS NDVI:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get comprehensive GEE disaster assessment
   * Combines multiple analyses for insurance claims
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} disasterDate - Date of the disaster event
   */
  getGeeDisasterAssessment: async (lat, lng, disasterDate) => {
    try {
      const response = await api.get('/satellite/gee/disaster-assessment', {
        params: { lat, lng, disasterDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GEE disaster assessment:', error);
      return { success: false, error: error.message };
    }
  },

  // ==========================================
  // LOCATION SEARCH APIs (Google Places)
  // ==========================================

  /**
   * Search for locations using Google Places Autocomplete
   * @param {string} query - Search query (e.g., city name)
   * @param {string} sessionToken - Optional session token for billing optimization
   */
  searchLocations: async (query, sessionToken = null) => {
    try {
      const params = { query };
      if (sessionToken) params.sessionToken = sessionToken;
      const response = await api.get('/satellite/location/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching locations:', error);
      return { success: false, predictions: [], error: error.message };
    }
  },

  /**
   * Get place details and coordinates from Google Place ID
   * @param {string} placeId - Google Place ID
   */
  getPlaceDetails: async (placeId) => {
    try {
      const response = await api.get('/satellite/location/place', {
        params: { placeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching place details:', error);
      return { success: false, error: error.message };
    }
  },

  // ==========================================
  // GOOGLE WEATHER API
  // ==========================================

  /**
   * Get weather from Google Weather API (primary source)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   */
  getGoogleWeather: async (lat, lng) => {
    try {
      const response = await api.get('/satellite/google-weather', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Google weather:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get forecast from Google Weather API
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} days - Number of forecast days (default: 7)
   */
  getGoogleForecast: async (lat, lng, days = 7) => {
    try {
      const response = await api.get('/satellite/google-forecast', {
        params: { lat, lng, days }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Google forecast:', error);
      return { success: false, error: error.message };
    }
  },

  // ==========================================
  // ADMIN WEATHER OVERRIDE APIs
  // ==========================================

  /**
   * Set admin override for weather data at a location
   * Allows administrators to manually correct weather readings
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {object} weatherData - Override weather data
   * @param {string} adminUser - Admin user name
   */
  setWeatherOverride: async (lat, lng, weatherData, adminUser) => {
    try {
      const response = await api.post('/satellite/weather/override', {
        lat,
        lng,
        weather: weatherData.weather,
        forecast: weatherData.forecast,
        adminUser
      });
      return response.data;
    } catch (error) {
      console.error('Error setting weather override:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Clear admin override for a location
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   */
  clearWeatherOverride: async (lat, lng) => {
    try {
      const response = await api.delete('/satellite/weather/override', {
        params: { lat, lng }
      });
      return response.data;
    } catch (error) {
      console.error('Error clearing weather override:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all active admin weather overrides
   */
  getWeatherOverrides: async () => {
    try {
      const response = await api.get('/satellite/weather/overrides');
      return response.data;
    } catch (error) {
      console.error('Error fetching weather overrides:', error);
      return { success: false, overrides: [], count: 0 };
    }
  },

  // ==========================================
  // PROPERTY EVALUATION APIs
  // ==========================================

  /**
   * Get comprehensive property evaluation using satellite + AI
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} address - Optional address string
   */
  evaluateProperty: async (lat, lng, address = '') => {
    try {
      const response = await api.post('/evaluation/property', { lat, lng, address });
      return response.data;
    } catch (error) {
      console.error('Error evaluating property:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get satellite-adjusted commodity market rates
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   */
  getCommodityRates: async (lat, lng) => {
    try {
      const response = await api.post('/evaluation/commodity-rates', { lat, lng });
      return response.data;
    } catch (error) {
      console.error('Error fetching commodity rates:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Analyze property using Gemini AI
   */
  analyzePropertyAI: async (lat, lng, propertyDetails = {}) => {
    try {
      const response = await api.post('/evaluation/analyze', { lat, lng, propertyDetails });
      return response.data;
    } catch (error) {
      console.error('Error analyzing property:', error);
      return { success: false, error: error.message };
    }
  }
};

export default satelliteApi;
